import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';

/**
 * Page scaffolding shared by every screen: one width scale, one gutter, one
 * header pattern. Pages compose these instead of defining their own
 * `PageContainer` and `Title`.
 */

export type PageWidth = 'sm' | 'md' | 'lg' | 'xl' | 'full';

const WIDTH: Record<PageWidth, string> = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
  full: '100%',
};

const Container = styled.div<{ $width: PageWidth; $canvas?: boolean; $flush?: boolean }>`
  ${tw`w-full min-w-0 mx-auto box-border`}
  max-width: ${({ $width }) => WIDTH[$width]};
  ${({ $flush }) => ($flush ? tw`px-0` : tw`px-4 md:px-6 lg:px-8`)}
  ${({ $flush }) => ($flush ? tw`py-0` : tw`py-6 md:py-8`)}
`;

const Canvas = styled.div`
  ${tw`w-full min-w-0 flex-1`}
  background-color: var(--color-bg-canvas);
  min-height: calc(100vh - 68px);
`;

/** Inline helpers for small layout needs inside pages (no `tw` prop needed). */
export const Box = styled.div<{ $mt?: number; $mb?: number; $minW0?: boolean }>`
  ${({ $minW0 }) => $minW0 && 'min-width: 0;'}
  ${({ $mt }) => $mt !== undefined && `margin-top: ${$mt * 4}px;`}
  ${({ $mb }) => $mb !== undefined && `margin-bottom: ${$mb * 4}px;`}
`;

export const Muted = styled.span<{ $size?: 'xs' | 'sm' }>`
  ${({ $size = 'sm' }) => ($size === 'xs' ? tw`text-xs` : tw`text-sm`)}
  color: var(--color-text-secondary);
`;

export const Strong = styled.span`
  ${tw`font-medium`}
  color: var(--color-text-primary);
`;

export const InlineRow = styled.div`
  ${tw`inline-flex items-center gap-2`}
`;

export interface PageContainerProps {
  children: ReactNode;
  /** Content width. `lg` (1024px) suits settings and lists, `xl` wide tables. */
  width?: PageWidth;
  /** Soft background behind the cards (dashboards, admin, settings). */
  canvas?: boolean;
  /** No padding; the page handles its own gutters. */
  flush?: boolean;
  className?: string;
  'data-testid'?: string;
}

export function PageContainer({
  children,
  width = 'xl',
  canvas = false,
  flush = false,
  className,
  ...rest
}: PageContainerProps) {
  const inner = (
    <Container $width={width} $flush={flush} className={className} {...rest}>
      {children}
    </Container>
  );
  return canvas ? <Canvas>{inner}</Canvas> : inner;
}

// ─── page header ────────────────────────────────────────────────────────────

const HeaderRoot = styled.header`
  ${tw`flex flex-col gap-4 mb-6 md:mb-8 md:flex-row md:items-end md:justify-between`}
`;

const HeaderMain = styled.div`
  ${tw`min-w-0 flex-1`}
`;

const Eyebrow = styled.div`
  ${tw`flex items-center gap-2 mb-2 text-xs font-semibold uppercase tracking-wider`}
  color: var(--color-text-secondary);
`;

const Title = styled.h1`
  ${tw`text-2xl md:text-3xl font-bold leading-tight`}
  color: var(--color-text-primary);
  font-family: var(--font-display);
  letter-spacing: -0.02em;
`;

const Description = styled.p`
  ${tw`mt-1.5 text-sm md:text-base max-w-2xl`}
  color: var(--color-text-secondary);
`;

const Actions = styled.div`
  ${tw`flex flex-wrap items-center gap-2 shrink-0`}
`;

const BackLink = styled(Link)`
  ${tw`inline-flex items-center gap-1.5 mb-3 text-sm font-medium no-underline`}
  color: var(--color-text-secondary);
  &:hover {
    color: var(--color-text-primary);
  }
`;

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  /** Small label above the title, e.g. "Admin" or a brand name. */
  eyebrow?: ReactNode;
  actions?: ReactNode;
  backTo?: { to: string; label: string };
  className?: string;
}

export function PageHeader({
  title,
  description,
  eyebrow,
  actions,
  backTo,
  className,
}: PageHeaderProps) {
  return (
    <HeaderRoot className={className}>
      <HeaderMain>
        {backTo && (
          <BackLink to={backTo.to}>
            <FontAwesomeIcon icon={faArrowLeft} size="sm" />
            {backTo.label}
          </BackLink>
        )}
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        <Title>{title}</Title>
        {description && <Description>{description}</Description>}
      </HeaderMain>
      {actions && <Actions>{actions}</Actions>}
    </HeaderRoot>
  );
}

// ─── section header (inside a page, between cards) ──────────────────────────

const SectionRoot = styled.div`
  ${tw`flex flex-col gap-2 mb-4 sm:flex-row sm:items-end sm:justify-between`}
`;

const SectionTitleText = styled.h2`
  ${tw`text-lg font-semibold leading-tight`}
  color: var(--color-text-primary);
  font-family: var(--font-display);
  letter-spacing: -0.01em;
`;

const SectionHint = styled.p`
  ${tw`mt-1 text-sm`}
  color: var(--color-text-secondary);
`;

export function SectionHeader({
  title,
  description,
  actions,
  className,
  level = 2,
}: {
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  className?: string;
  /** 2 between cards on a page; 3 when it sits inside a card whose title is an h2. */
  level?: 2 | 3;
}) {
  return (
    <SectionRoot className={className}>
      <div>
        <SectionTitleText as={`h${level}` as 'h2' | 'h3'}>{title}</SectionTitleText>
        {description && <SectionHint>{description}</SectionHint>}
      </div>
      {actions && <Actions>{actions}</Actions>}
    </SectionRoot>
  );
}

/** Vertical rhythm between stacked cards and sections. */
export const Stack = styled.div<{ $gap?: 4 | 6 | 8 }>`
  ${tw`flex flex-col`}
  gap: ${({ $gap = 6 }) => `${$gap * 4}px`};
`;
